import React, { useState } from 'react';
import { Phone, ExternalLink, Clock, Navigation, ChevronDown, ChevronUp, Headphones, Globe, Loader2, PhoneCall } from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL || '';

function PhoneButton({ label, number, primary = false, onCall }) {
  const [calling, setCalling] = useState(false);
  const [status, setStatus] = useState('');
  if (!number) return null;

  async function handleClick() {
    if (onCall) {
      setCalling(true);
      setStatus('');
      try {
        const res = await fetch(`${BASE}/calls/initiate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ clientPhone: number.replace(/[^0-9+]/g, '') }),
        });
        const data = await res.json();
        if (data.ok) {
          setStatus('Ringing your phone...');
          setTimeout(() => { setCalling(false); setStatus(''); }, 8000);
        } else {
          setStatus(data.error || 'Call failed');
          setTimeout(() => { setCalling(false); setStatus(''); }, 4000);
        }
      } catch {
        setStatus('Call failed');
        setTimeout(() => { setCalling(false); setStatus(''); }, 4000);
      }
    }
  }

  return (
    <button
      onClick={handleClick}
      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all text-left ${
        calling
          ? 'bg-green-50 border-green-300 ring-2 ring-green-200'
          : primary
            ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
            : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
      }`}
    >
      <div>
        <div className={`text-xs font-medium ${calling ? 'text-green-700' : primary ? 'text-emerald-700' : 'text-slate-600'}`}>
          {status || label}
        </div>
        <div className={`text-sm font-bold font-mono ${calling ? 'text-green-800' : primary ? 'text-emerald-800' : 'text-slate-800'}`}>{number}</div>
      </div>
      {calling ? (
        <Loader2 size={16} className="text-green-500 animate-spin" />
      ) : (
        <PhoneCall size={16} className={primary ? 'text-emerald-500' : 'text-slate-400'} />
      )}
    </button>
  );
}

export default function CarrierCommandCenter({ carrier, clientPlanType, apiActions, carriers = [], onCarrierSelect }) {
  const [showIVR, setShowIVR] = useState(false);

  if (!carrier) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-2">
          <Headphones size={15} className="text-slate-400" />
          <h3 className="text-sm font-bold text-slate-800">Carrier Tools</h3>
        </div>
        {carriers.length > 0 && onCarrierSelect ? (
          <>
            <p className="text-xs text-slate-500 mb-2">No carrier auto-matched. Select one:</p>
            <select
              onChange={(e) => {
                const c = carriers.find(cr => String(cr.id) === e.target.value);
                if (c) onCarrierSelect(c);
              }}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none"
              defaultValue=""
            >
              <option value="" disabled>Choose carrier...</option>
              {carriers.map(c => (
                <option key={c.id} value={c.id}>{c.carrier_name}</option>
              ))}
            </select>
          </>
        ) : (
          <p className="text-xs text-slate-500">No carrier matched for this client.</p>
        )}
      </div>
    );
  }

  // Determine primary phone based on plan type
  const planType = (clientPlanType || '').toLowerCase();
  let primaryPhone = carrier.general_support_phone;
  let primaryLabel = 'Agent Support';

  if (planType.includes('ma') || planType.includes('mapd') || planType.includes('dsnp') || planType.includes('csnp') || planType.includes('hmo') || planType.includes('ppo')) {
    if (carrier.ma_support_phone) {
      primaryPhone = carrier.ma_support_phone;
      primaryLabel = 'MA Agent Line';
    }
  } else if (planType.includes('medsup') || planType.includes('medigap') || planType.includes('supplement')) {
    if (carrier.medsup_support_phone) {
      primaryPhone = carrier.medsup_support_phone;
      primaryLabel = 'MedSup Agent Line';
    }
  } else if (planType.includes('pdp') || planType.includes('part d')) {
    if (carrier.pdp_support_phone) {
      primaryPhone = carrier.pdp_support_phone;
      primaryLabel = 'PDP Support';
    }
  }

  // Collect all available phones (excluding primary to avoid duplicate)
  const otherPhones = [];
  if (carrier.ma_support_phone && carrier.ma_support_phone !== primaryPhone)
    otherPhones.push({ label: 'MA Agent Line', number: carrier.ma_support_phone });
  if (carrier.medsup_support_phone && carrier.medsup_support_phone !== primaryPhone)
    otherPhones.push({ label: 'MedSup Line', number: carrier.medsup_support_phone });
  if (carrier.pdp_support_phone && carrier.pdp_support_phone !== primaryPhone)
    otherPhones.push({ label: 'PDP Support', number: carrier.pdp_support_phone });
  if (carrier.general_support_phone && carrier.general_support_phone !== primaryPhone)
    otherPhones.push({ label: 'General Line', number: carrier.general_support_phone });

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-teal-50">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Headphones size={15} className="text-emerald-600" />
          {carrier.carrier_name}
          {carrier.carrier_code && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-600">
              {carrier.carrier_code}
            </span>
          )}
        </h3>
      </div>

      <div className="p-3 space-y-2">
        {/* Primary phone */}
        <PhoneButton label={primaryLabel} number={primaryPhone} primary onCall />

        {/* Other phones */}
        {otherPhones.map((p, i) => (
          <PhoneButton key={i} label={p.label} number={p.number} onCall />
        ))}

        {/* Agent Portal — prominent, right after phones */}
        {carrier.portal_url && (
          <a
            href={carrier.portal_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-3 py-3 bg-indigo-50 border-2 border-indigo-200 rounded-lg hover:bg-indigo-100 hover:border-indigo-300 transition-all"
          >
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-indigo-600" />
              <div>
                <span className="text-sm font-bold text-indigo-700">Agent Portal</span>
                <div className="text-[10px] text-indigo-500 truncate max-w-[180px]">{carrier.portal_url.replace(/^https?:\/\//, '')}</div>
              </div>
            </div>
            <ExternalLink size={16} className="text-indigo-400" />
          </a>
        )}

        {/* Hours */}
        {carrier.hours_of_operation && (
          <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
            <Clock size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <span className="text-xs text-blue-700">{carrier.hours_of_operation}</span>
          </div>
        )}

        {/* IVR Tips */}
        {carrier.ivr_tips && (
          <div className="rounded-lg border border-slate-100">
            <button
              onClick={() => setShowIVR(!showIVR)}
              className="flex items-center justify-between w-full px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors rounded-lg"
            >
              <span className="flex items-center gap-1.5">
                <Navigation size={13} className="text-purple-500" />
                IVR Navigation Tips
              </span>
              {showIVR ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showIVR && (
              <div className="px-3 pb-2 text-xs text-slate-600 whitespace-pre-line leading-relaxed border-t border-slate-50 pt-2">
                {carrier.ivr_tips}
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {carrier.notes && (
          <div className="px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg">
            <span className="text-xs text-amber-700">{carrier.notes}</span>
          </div>
        )}

        {/* API Actions slot (for future Humana API etc) */}
        {apiActions && apiActions.length > 0 && (
          <div className="pt-2 border-t border-slate-100 space-y-1.5">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">API Actions</span>
            {apiActions.map((action, i) => (
              <button
                key={i}
                onClick={action.onClick}
                className="flex items-center gap-2 w-full px-3 py-2 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 text-xs font-medium text-violet-700 transition-colors"
              >
                {action.icon && <action.icon size={14} />}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
